'use client';

import {
  KeyboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { usePathname } from 'next/navigation';
import { useSiteSearch, SiteSearchResult } from '@/hooks/useSiteSearch';
import { trackSearchEvent } from '@/utils/searchAnalytics';
import SearchIcon from './components/SearchIcon';
import CloseIcon from './components/CloseIcon';
import SearchResultItem from './components/SearchResultItem';
import EmptyState from './components/EmptyState';
import PopularSearches from './components/PopularSearches';
import styles from './searchBar.module.scss';

type Variant = 'header' | 'fullscreen';

type Props = {
  variant?: Variant;
  placeholder?: string;
  autoFocus?: boolean;
  onResultSelect?: () => void;
  className?: string;
};

const SearchBar = ({
  variant = 'header',
  placeholder = 'Search the site',
  autoFocus = false,
  onResultSelect,
  className = '',
}: Props) => {
  const {
    query,
    debouncedQuery,
    setQuery,
    results,
    isReady,
    hasQuery,
    hasResults,
    reset,
  } = useSiteSearch();

  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const pathname = usePathname();

  const showDropdown = isOpen;
  const showResults = hasQuery && hasResults;
  const showEmptyState = hasQuery && !hasResults;
  const showPopular = !hasQuery;

  useEffect(() => {
    if (autoFocus) {
      inputRef.current?.focus();
    }
  }, [autoFocus]);

  useEffect(() => {
    setIsOpen(false);
    reset();
  }, [pathname, reset]);

  useEffect(() => {
    setActiveIndex(-1);
  }, [results]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!isReady) return;
    const term = debouncedQuery.trim();
    if (term.length < 2) return;

    trackSearchEvent({
      event: 'search_query',
      search_term: term,
      results_count: results.length,
    });

    if (results.length === 0) {
      trackSearchEvent({
        event: 'search_empty',
        search_term: term,
      });
    }
  }, [debouncedQuery, results, isReady]);

  const handleSelect = useCallback(
    (result?: SiteSearchResult) => {
      if (result) {
        const position = results.findIndex(
          (r) => r.item.id === result.item.id
        );
        trackSearchEvent({
          event: 'search_result_click',
          search_term: debouncedQuery.trim(),
          result_href: result.item.href,
          result_title: result.item.title,
          result_position: position >= 0 ? position + 1 : 0,
        });
      }

      setIsOpen(false);
      reset();
      onResultSelect?.();
    },
    [reset, onResultSelect, results, debouncedQuery]
  );

  const handleFocus = () => {
    setIsOpen(true);
    trackSearchEvent({ event: 'search_opened' });
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (!showDropdown) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((prev) =>
        results.length === 0 ? -1 : (prev + 1) % results.length
      );
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((prev) =>
        results.length === 0 ? -1 : (prev - 1 + results.length) % results.length
      );
    } else if (event.key === 'Enter') {
      if (activeIndex >= 0 && results[activeIndex]) {
        event.preventDefault();
        const selected = results[activeIndex];
        handleSelect(selected);
        window.location.href = selected.item.href;
      }
    } else if (event.key === 'Escape') {
      setIsOpen(false);
      inputRef.current?.blur();
    }
  };

  const handleClear = () => {
    reset();
    setActiveIndex(-1);
    inputRef.current?.focus();
  };

  return (
    <div
      ref={containerRef}
      className={`${styles.root} ${styles[`variant_${variant}`]} ${className}`}
      data-variant={variant}
    >
      <div className={styles.inputContainer}>
        <SearchIcon className={styles.searchIcon} />
        <input
          ref={inputRef}
          type="text"
          className={styles.input}
          placeholder={placeholder}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
          }}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          autoComplete="off"
          aria-label="Search the site"
          aria-expanded={showDropdown}
          aria-controls="search-results"
          role="combobox"
        />
        {query.length > 0 && (
          <button
            type="button"
            className={styles.clearButton}
            onClick={handleClear}
            aria-label="Clear search"
          >
            <CloseIcon />
          </button>
        )}
      </div>

      {showDropdown && (
        <div
          id="search-results"
          className={styles.dropdown}
          role="listbox"
        >
          {showResults &&
            results.map((result, index) => (
              <SearchResultItem
                key={result.item.id}
                result={result}
                isActive={index === activeIndex}
                onSelect={handleSelect}
                onMouseEnter={() => setActiveIndex(index)}
              />
            ))}
          {showEmptyState && <EmptyState query={query} />}
          {showPopular && <PopularSearches onSelect={handleSelect} />}
        </div>
      )}
    </div>
  );
};

export default SearchBar;
